import React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronLeft, ChevronRight, Copy, Pencil, ThumbsDown, ThumbsUp } from "lucide-react";

const ICON_BTN =
  "inline-flex h-7 w-7 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:cursor-not-allowed disabled:opacity-40";

const FEEDBACK_UP =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 dark:hover:bg-emerald-950 dark:hover:text-emerald-300 disabled:cursor-not-allowed disabled:opacity-40";

const FEEDBACK_DOWN =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 dark:bg-rose-950/50 dark:text-rose-400 dark:hover:bg-rose-950 dark:hover:text-rose-300 disabled:cursor-not-allowed disabled:opacity-40";

const HOVER_REVEAL =
  "opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto";

export function ChatMessageToolbar({
  align,
  copyText,
  canEdit,
  onEdit,
  siblingIndex,
  siblingTotal,
  onPrevSibling,
  onNextSibling,
  disabled,
  feedback,
  onFeedback,
}: {
  align: "left" | "right";
  copyText: string;
  canEdit?: boolean;
  onEdit?: () => void;
  siblingIndex?: number;
  siblingTotal?: number;
  onPrevSibling?: () => void;
  onNextSibling?: () => void;
  disabled?: boolean;
  /** Eigene Bewertung der Antwort; nur mit onFeedback sichtbar. */
  feedback?: "up" | "down" | null;
  onFeedback?: (rating: "up" | "down") => void;
}) {
  const [copied, setCopied] = React.useState(false);
  const copiedTimerRef = React.useRef<number | null>(null);
  const showPager = (siblingTotal ?? 1) > 1;

  React.useEffect(() => {
    return () => {
      if (copiedTimerRef.current !== null) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const copy = async () => {
    const text = copyText.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (copiedTimerRef.current !== null) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={`mt-1 flex items-center gap-0.5 ${align === "right" ? "justify-end" : "justify-start"}`}>
      {copied && typeof document !== "undefined"
        ? createPortal(
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-pipedrive-blue px-3 py-1.5 text-sm text-white shadow-lg md:-translate-x-[calc(130px+var(--app-nav-width,3.5rem)/2)]"
              role="status"
              aria-live="polite"
            >
              <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Kopiert
            </div>,
            document.getElementById("chat-copy-toast-host") ?? document.body,
          )
        : null}
      {showPager ? (
        <div className="mr-1 inline-flex items-center gap-0.5 text-xs tabular-nums text-app-text-subtle">
          <button
            type="button"
            className={ICON_BTN}
            aria-label="Vorherige Version"
            disabled={disabled || (siblingIndex ?? 1) <= 1}
            onClick={onPrevSibling}
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          </button>
          <span className="min-w-[2.25rem] text-center">
            {siblingIndex} / {siblingTotal}
          </span>
          <button
            type="button"
            className={ICON_BTN}
            aria-label="Nächste Version"
            disabled={disabled || (siblingIndex ?? 1) >= (siblingTotal ?? 1)}
            onClick={onNextSibling}
          >
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : null}
      <div className={`inline-flex items-center gap-0.5 ${HOVER_REVEAL}`}>
        <button
          type="button"
          className={ICON_BTN}
          aria-label={copied ? "Kopiert" : "Kopieren"}
          title={copied ? "Kopiert" : "Kopieren"}
          disabled={disabled || !copyText.trim()}
          onClick={() => void copy()}
        >
          {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
        </button>
        {canEdit && onEdit ? (
          <button
            type="button"
            className={ICON_BTN}
            aria-label="Bearbeiten"
            title="Bearbeiten"
            disabled={disabled}
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {onFeedback ? (
        <div className={`inline-flex items-center gap-0.5 ${feedback ? "" : HOVER_REVEAL}`}>
          <button
            type="button"
            className={feedback === "up" ? FEEDBACK_UP : ICON_BTN}
            aria-label="Hilfreich"
            aria-pressed={feedback === "up"}
            title="Hilfreich"
            disabled={disabled}
            onClick={() => onFeedback("up")}
          >
            <ThumbsUp className="h-3.5 w-3.5" fill={feedback === "up" ? "currentColor" : "none"} aria-hidden />
          </button>
          <button
            type="button"
            className={feedback === "down" ? FEEDBACK_DOWN : ICON_BTN}
            aria-label="Nicht hilfreich"
            aria-pressed={feedback === "down"}
            title="Nicht hilfreich"
            disabled={disabled}
            onClick={() => onFeedback("down")}
          >
            <ThumbsDown className="h-3.5 w-3.5" fill={feedback === "down" ? "currentColor" : "none"} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}
